import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { SOFTWARE_DEV_DATA, SOFTWARE_DEV_PAGE_DATA } from "@/data/siteData";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  Globe, Smartphone, Cloud, Workflow, Landmark, Heart, BookOpen,
  ShoppingCart, Truck, Building, ArrowRight, Quote, CheckCircle, ChevronLeft, ChevronRight
} from "lucide-react";

const iconMap = { Globe, Smartphone, Cloud, Workflow, Landmark, Heart, BookOpen, ShoppingCart, Truck, Building };

export default function SoftwareDevelopment() {
  const { hero, customSolutions, domains, process, caseStudies } = SOFTWARE_DEV_DATA;
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [formData, setFormData] = useState({ name: "", role: "", category: "client", program: "", outcome: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase.from("testimonials").insert({
        name: formData.name,
        role: formData.role,
        category: formData.category,
        program: formData.program,
        outcome: formData.outcome,
        status: "pending",
      });
      if (error) throw error;
      toast.success("Thank you for your feedback! Your testimonial has been submitted.");
      setFormData({ name: "", role: "", category: "client", program: "", outcome: "" });
    } catch {
      toast.error("Could not submit right now. Please try again later.");
    }
  };

  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(() => {
      setTestimonialIdx((p) => (p + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    setTestimonialIdx(0);
  }, [testimonials.length]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoadingTestimonials(true);
      try {
        const { data, error } = await supabase
          .from("testimonials")
          .select("id,name,role,category,program,outcome,status")
          .eq("status", "approved")
          .eq("category", "client")
          .order("approved_at", { ascending: false });

        if (error) throw error;
        if (cancelled) return;
        setTestimonials(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setTestimonials([]);
      } finally {
        if (!cancelled) setLoadingTestimonials(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <Toaster position="top-right" />

      <section className="relative py-24 md:py-32 overflow-hidden mesh-bg" data-testid="software-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#097DC3]/8 text-[#097DC3] text-sm font-semibold uppercase tracking-wider mb-6">
              {hero.tagline}
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0A0A0A] mb-6">
              {hero.title}
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#475569] mb-10 max-w-2xl">
              {hero.subtitle}
            </p>
            <Link
              to="/contact"
              className="btn-pill inline-flex items-center gap-2 bg-[#097DC3] text-white px-8 py-3.5 rounded-full font-semibold text-sm"
              data-testid="software-hero-cta"
            >
              Request a Proposal <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#8CDDF6]/15 blur-3xl" />
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="custom-solutions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{SOFTWARE_DEV_PAGE_DATA.sections.customSolutions.label}</span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
              {SOFTWARE_DEV_PAGE_DATA.sections.customSolutions.title}
            </h2>
            <p className="text-base md:text-lg text-[#475569] max-w-2xl mb-16">
              {SOFTWARE_DEV_PAGE_DATA.sections.customSolutions.description}
            </p>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {customSolutions.map((sol) => {
              const Icon = iconMap[sol.icon];
              return (
                <StaggerItem key={sol.title}>
                  <div className="card-lift bg-white p-8 rounded-2xl border border-slate-100 shadow-sm flex gap-5">
                    <div className="w-12 h-12 rounded-xl bg-[#097DC3]/8 flex items-center justify-center shrink-0">
                      <Icon size={24} className="text-[#097DC3]" />
                    </div>
                    <div>
                      <h3 className="font-['Outfit'] text-xl font-semibold text-[#0A0A0A] mb-2">{sol.title}</h3>
                      <p className="text-sm text-[#475569] leading-relaxed">{sol.description}</p>
                    </div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="dev-process-section" style={{ backgroundImage: SOFTWARE_DEV_PAGE_DATA.sections.process.backgroundImage, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{SOFTWARE_DEV_PAGE_DATA.sections.process.label}</span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-16">
              {SOFTWARE_DEV_PAGE_DATA.sections.process.title}
            </h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((step) => (
              <StaggerItem key={step.step}>
                <div className="relative p-8 rounded-2xl border border-slate-100 bg-white shadow-sm card-lift">
                  <span className="font-['Outfit'] text-5xl font-bold text-[#8CDDF6]/30 absolute top-4 right-6">
                    {step.step}
                  </span>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#0A0A0A] mb-3 mt-2">{step.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{step.description}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="domains-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{SOFTWARE_DEV_PAGE_DATA.sections.domains.label}</span>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-16">
              {SOFTWARE_DEV_PAGE_DATA.sections.domains.title}
            </h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {domains.map((domain) => {
              const Icon = iconMap[domain.icon];
              return (
                <StaggerItem key={domain.name}>
                  <div className="card-lift bg-white h-full p-6 rounded-2xl border border-slate-100 shadow-sm text-center group cursor-default">
                    <div className="w-14 h-14 rounded-xl bg-[#097DC3]/8 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#097DC3] transition-colors duration-300">
                      <Icon size={24} className="text-[#097DC3] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <p className="font-semibold text-sm text-[#0A0A0A]">{domain.name}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      <section
  className="py-12 md:py-16 relative bg-fixed bg-center bg-cover"
  style={{
    backgroundImage: SOFTWARE_DEV_PAGE_DATA.sections.feedback.backgroundImage
  }} data-testid="client-feedback-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="text-center mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{SOFTWARE_DEV_PAGE_DATA.sections.feedback.label}</span>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ScrollReveal>
              <div>
                <h3 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
                  {SOFTWARE_DEV_PAGE_DATA.sections.feedback.formTitle}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{SOFTWARE_DEV_PAGE_DATA.sections.feedback.fields.fullName}</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                      placeholder={SOFTWARE_DEV_PAGE_DATA.sections.feedback.fields.fullNamePlaceholder}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{SOFTWARE_DEV_PAGE_DATA.sections.feedback.fields.role}</label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                      placeholder={SOFTWARE_DEV_PAGE_DATA.sections.feedback.fields.rolePlaceholder}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{SOFTWARE_DEV_PAGE_DATA.sections.feedback.fields.serviceUsed}</label>
                    <input
                      type="text"
                      value={formData.program}
                      onChange={(e) => setFormData({ ...formData, program: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                      placeholder={SOFTWARE_DEV_PAGE_DATA.sections.feedback.fields.serviceUsedPlaceholder}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{SOFTWARE_DEV_PAGE_DATA.sections.feedback.fields.outcome}</label>
                    <textarea
                      value={formData.outcome}
                      onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow resize-none"
                      placeholder={SOFTWARE_DEV_PAGE_DATA.sections.feedback.fields.outcomePlaceholder}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn-pill bg-[#097DC3] text-white px-8 py-3 rounded-full font-semibold text-sm"
                  >
                    {SOFTWARE_DEV_PAGE_DATA.sections.feedback.submitButton}
                  </button>
                </form>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right">
              <div>
                <h3 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-[#0A0A0A] mb-8">
                  {SOFTWARE_DEV_PAGE_DATA.sections.feedback.testimonialsTitle}
                </h3>
                <div className="bg-white p-8 rounded-2xl border-l-4 border-[#3DC0E7] shadow-sm min-h-[400px] flex flex-col justify-between">
                  {loadingTestimonials ? (
                    <div>
                      <Quote size={32} className="text-[#8CDDF6] mb-6" />
                      <p className="text-lg text-[#475569] leading-relaxed mb-8">{SOFTWARE_DEV_PAGE_DATA.sections.feedback.loading}</p>
                    </div>
                  ) : testimonials.length === 0 ? (
                    <div>
                      <Quote size={32} className="text-[#8CDDF6] mb-6" />
                      <p className="text-lg text-[#475569] leading-relaxed mb-8">{SOFTWARE_DEV_PAGE_DATA.sections.feedback.empty}</p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Quote size={32} className="text-[#8CDDF6] mb-6" />
                        <p className="text-lg md:text-xl text-[#0A0A0A] leading-relaxed mb-8 min-h-[80px]">
                          {testimonials[testimonialIdx]?.outcome}
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center gap-4 mb-6">
                          <img
                            src={SOFTWARE_DEV_PAGE_DATA.sections.feedback.avatarSrc}
                            alt={testimonials[testimonialIdx]?.name || SOFTWARE_DEV_PAGE_DATA.sections.feedback.avatarAlt}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-semibold text-[#0A0A0A]">{testimonials[testimonialIdx]?.name}</p>
                            <p className="text-sm text-[#475569]">{testimonials[testimonialIdx]?.role}</p>
                            {testimonials[testimonialIdx]?.program && (
                              <p className="text-xs text-[#097DC3] font-medium mt-0.5">{testimonials[testimonialIdx].program}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2 flex-wrap">
                            {testimonials.map((_, i) => (
                              <button
                                key={testimonials[i]?.id || i}
                                onClick={() => setTestimonialIdx(i)}
                                className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${
                                  i === testimonialIdx ? "bg-[#097DC3]" : "bg-[#E2E8F0]"
                                }`}
                              />
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setTestimonialIdx((p) => (p - 1 + testimonials.length) % testimonials.length)}
                              className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <button
                              onClick={() => setTestimonialIdx((p) => (p + 1) % testimonials.length)}
                              className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                            >
                              <ChevronRight size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#097DC3] relative overflow-hidden" data-testid="request-proposal-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-white/20 blur-3xl -translate-x-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-white/20 blur-3xl translate-x-1/2" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 text-center">
          <ScrollReveal>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">
              {SOFTWARE_DEV_PAGE_DATA.sections.cta.title}
            </h2>
            <p className="text-lg text-[#8CDDF6] mb-10 max-w-xl mx-auto">
              {SOFTWARE_DEV_PAGE_DATA.sections.cta.description}
            </p>
            <Link
              to="/contact"
              className="btn-pill inline-flex items-center gap-2 bg-white text-[#097DC3] px-8 py-3.5 rounded-full font-semibold text-sm"
              data-testid="request-proposal-cta"
            >
              {SOFTWARE_DEV_PAGE_DATA.sections.cta.button} <ArrowRight size={16} />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
